import axios from 'axios';
import classNames from 'classnames';
import { useMemo, useState } from 'react';

import ConfirmationCode from 'components/ConfirmationCode/ConfirmationCode';
import CategoriesPicker from '../../pages/Signup/CategoriesPicker';
import Loader from 'components/Base/Loader';

import IconStepCheck from './img/step-check.svg';
import IconCat from './img/cat-icon.svg';
import Check from './img/check.svg';
import { countdown, errorNotification, parseError } from 'utils/helpers';
import { currentUSStateCodes, USStateCodes } from 'utils/constants';
import { Link } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';

import logo from '../../pages/Signup/img/logo.svg';

const Stepper = ({
	step,
	nextStep,
	userType,
	signInfo,
	finish,
	categories,
	selectCategory,
	isGoogleAuth,
	dob,
	onDobChange,
	address,
	onAddressChange,
	isAddressVerified,
	setIsAddressVerified,
	setCategoryRate,
	setRate,
	rate,
	phoneNumber,
	setPhoneNumber,
	setIsPhoneVerified,

}) => {
	/** STATES */
	const [waitConfirmEmail, setWaitConfirmEmail] = useState(false);
	const [confirmEmail, setConfirmEmail] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [timeLeft, setTimeLeft] = useState(0);
	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('')
	const [showOtpInput, setShowOtpInput] = useState(false);
	const [phoneCode, setPhoneCode] = useState('');

	const sendEmailConfirmation = () => {
		if (!waitConfirmEmail) {
			setIsLoading(true);
			axios
				.post('/auth/signup/verify/send', { email: signInfo.email })
				.then(() => {
					setWaitConfirmEmail(true);
					// Start cooldown for resend
					setTimeLeft(60);
					startResendTimer();
				})
				.catch((error) => {
					console.log(error);
					errorNotification(error, true);
				})
				.finally(() => {
					setIsLoading(false);
				});
		} else {
			const confirmFilled = confirmEmail.every((i) => i.length);
			if (confirmFilled) {
				setIsLoading(true);
				axios
					.post('/auth/signup/verify/enter', {
						email: signInfo.email,
						code: confirmEmail.join(''),
					})
					.then(() => {
						nextStep();
					})
					.catch((error) => {
						errorNotification(error, true);
						setConfirmEmail([]);
					})
					.finally(() => {
						setIsLoading(false);
					});
			}
		}
	};

	// Function to resend the confirmation email
	const resendEmailConfirmation = () => {
		setIsLoading(true);
		axios
			.post('/auth/signup/verify/send', { email: signInfo.email })
			.then(() => {
				setTimeLeft(60); // Reset the cooldown timer
				startResendTimer(); // Restart the resend timer
			})
			.catch((error) => {
				console.log(error);
				errorNotification(error, true);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	// Timer function for cooldown period
	const startResendTimer = () => {
		const timer = setInterval(() => {
			setTimeLeft((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	};


	const confirmEmailFilled = useMemo(
		() => confirmEmail.every((i) => i.length),
		[confirmEmail]
	);

	const validatePhoneNumber = async () => {
		if (!phoneNumber) {
			setError({ message: "Phone number is required." });
			return;
		}
		try {
			setError({})
			await axios.post("/auth/signup/number/check", {
				resend: showOtpInput, number: phoneNumber
			});

			setTimeLeft(60)
			countdown(setTimeLeft)
			setShowOtpInput(true);
		} catch (error) {
			setError(parseError(error));
			console.error("Failed to send OTP:", error);
		}
	};

	const confirmPhoneValidateCode = async () => {
		if (!phoneCode) {
			setError({ message: "Verification code is required." });
			return;
		}
		try {
			const response = await axios.post('/auth/signup/number/confirm', { code: phoneCode, number: phoneNumber });
			if (response.status === 200) {
				setShowOtpInput(false);
				setIsPhoneVerified(true);
				nextStep();
			} else {
				setError("Invalid code");
			}
			setShowOtpInput(false);
			setIsPhoneVerified(true);
		} catch (error) {
			setError(parseError(error));
			console.error("Validation failed", error);
		}
	};


	const isAddressComplete = () => {
		if (
			!address.addressLine1 ||
			!address.city ||
			!address.state ||
			!address.zipCode
		) {
			return false;
		}
		return true;
	};

	const verifyAddress = () => {
		axios
			.post('/auth/signup/address/confirm', { location: address })
			.then(({ data }) => {
				setIsAddressVerified(true);
				setSuccessMessage("Address successfully verified");

				setTimeout(() => {
					setSuccessMessage("");
				}, 5000);
			})
			.catch((error) => {
				const errorMessage = parseError(error);
				setError(errorMessage);
				console.log(error.response);

				setTimeout(() => {
					setError("");
				}, 5000);
			});
	};


	return (
		<div className="w-full max-w-3xl mx-auto p-6 bg-white rounded-2xl shadow-md">
			{/* Header with logo and name */}
			<div className="sm:mx-auto sm:w-full sm:max-w-md">
				<a href="/" className="flex justify-center items-center space-x-2 mb-8">
					<img
						src={logo}
						alt="Emissary Care"
						className="h-12 w-auto"
					/>
					{/* <span className="text-xl font-semibold bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
						Emissary Care
					</span> */}
				</a>
				{/* <h2 className="mt-6 text-center text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
					Finish registration in a few steps
				</h2> */}
			</div>

			{/* Horizontal step indicators */}
			<div className="flex justify-center gap-4 mb-8 flex-wrap">
				<div className={classNames(
					'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
					step === 1 ? 'bg-blue-100 text-blue-700' :
						'bg-slate-100 text-slate-500'
				)}>
					<div className={classNames(
						'w-5 h-5 rounded-full border-[1px] flex items-center justify-center',
						step === 1 ? 'border-blue-500' :
							'border-slate-500 bg-transparent'
					)}>
						{step > 1 &&
							<img src={Check} alt="Step icon" className="w-3 h-3 text-green-700" />
						}
					</div>
					Confirm Email
				</div>



				<div className={classNames(
					'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
					step === 2 ? 'bg-blue-100 text-blue-700' :
						'bg-slate-100 text-slate-500'
				)}>
					<div className={classNames(
						'w-5 h-5 rounded-full border-[1px] flex items-center justify-center',
						step === 2 ? 'border-blue-500' :
							'border-slate-500 bg-transparent'
					)}>
						{step > 2 &&
							<img src={Check} alt="Step icon" className="w-3 h-3 text-green-700" />
						}
					</div>
					Confirm phone number
				</div>

				{userType === 'caregiver' && (
					<>
						<div className={classNames(
							'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
							step === 3 ? 'bg-blue-100 text-blue-700' :
								'bg-slate-100 text-slate-500'
						)}>
							<div className={classNames(
								'w-5 h-5 rounded-full border-[1px] flex items-center justify-center',
								step === 3 ? 'border-blue-500' :
									'border-slate-500 bg-transparent'
							)}>
								{step > 3 &&
									<img src={Check} alt="Step icon" className="w-3 h-3 text-green-700" />
								}
							</div>
							Services Provided
						</div>
						<div className={classNames(
							'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
							step === 4 ? 'bg-blue-100 text-blue-700' :
								'bg-slate-100 text-slate-500'
						)}>
							<div className={classNames(
								'w-5 h-5 rounded-full border-[1px] flex items-center justify-center',
								step === 4 ? 'border-blue-500' :
									'border-slate-500 bg-transparent'
							)}>
								{step > 4 &&
									<img src={Check} alt="Step icon" className="w-3 h-3 text-green-700" />
								}
							</div>
							Personal Information
						</div>
					</>
				)}
			</div>

			{/* Content Area */}
			<div className="w-full">
				{/* ...your step content logic here (step === 1, 2, etc.) */}
				<div className="">
					<div className="text-center mb-8">
						{/* <h2 className="text-2xl font-bold text-gray-900">
							{step === 1 ? 'Confirm your email' :
								step === 2 ? 'Confirm your phone number' :
									step === 3 ? '' :
										step === 4 ? 'Step 4 Title Here' : 'Final Step Title Here'}
						</h2> */}
						<p className="text-gray-600">
							{step === 1 ? 'To complete registration, please confirm your email' :
								'To complete registration, please confirm your phone number'
							}
						</p>
					</div>

					<div className="flex flex-col justify-center md:flex-row gap-6">


						<div className="w-full md:w-2/3">
							{/* Dynamic content for steps (step === 1, step === 2, etc.) goes here */}
							{step === 1 && (
								<div className="flex flex-col justify-center items-center gap-4">
									<div className="text-lg font-semibold text-gray-800">{signInfo.email}</div>
									{waitConfirmEmail ? (
										<ConfirmationCode setConfirm={setConfirmEmail} />
									) : (
										<p className="text-gray-600">
											A confirmation code will be sent to your email. If you don't see it in your inbox, check the Promotions and the Spam folders.
										</p>
									)}


									{waitConfirmEmail && timeLeft === 0 && (
										<div className="flex items-center gap-4 mt-4">
											<button
												className="text-blue-600 hover:text-blue-800"
												onClick={resendEmailConfirmation} // Function to resend email
											>
												Resend confirmation code
											</button>
										</div>
									)}

									{waitConfirmEmail && timeLeft > 0 && (
										<div className="flex items-center gap-4 mt-4">
											<div className="timer-container">
												<p className="timer-text">Resend available in:</p>
												<span className="timer">{timeLeft}s</span>
											</div>
										</div>
									)}
									<button
										className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
										onClick={sendEmailConfirmation}
										disabled={waitConfirmEmail && !confirmEmailFilled}
									>
										{waitConfirmEmail ? 'Apply' : 'Send confirmation code'}
									</button>
								</div>
							)}
							{step === 2 && (
								<div className="flex flex-col items-center gap-4">
									<div className="text-lg font-semibold text-gray-800">
										We need your phone number to get started. You'll receive a verification code to confirm your number.
									</div>
									{error.message && <div className="text-red-500">{error.message}</div>}

									<PhoneInput
										className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 phone-input"
										country={'us'}
										specialLabel=""
										value={phoneNumber.toString()}
										onChange={(phone) => setPhoneNumber(phone)}
									/>

									{!showOtpInput && (
										<button
											className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
											onClick={validatePhoneNumber}
										>
											Receive code
										</button>
									)}

									{showOtpInput && (
										<>
											<ConfirmationCode setConfirm={setPhoneCode} />

											{timeLeft > 0 ? (
												<div className="flex items-center gap-4 mt-2">
													<div className="timer-container text-gray-600">
														<p className="timer-text">Resend available in:</p>
														<span className="font-medium">{timeLeft}s</span>
													</div>
												</div>
											) : (
												<div className="flex items-center gap-4 mt-2">
													<button
														className="text-blue-600 hover:text-blue-800"
														onClick={validatePhoneNumber}
													>
														Resend confirmation code
													</button>
												</div>
											)}

											<button
												className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
												onClick={confirmPhoneValidateCode}
												disabled={!phoneCode}
											>
												Confirm code
											</button>
										</>
									)}
								</div>
							)}

							{step === 3 && userType === 'customer' && (
								<div className="flex flex-col items-center gap-4">
									<label htmlFor="state" className="text-lg font-semibold text-gray-800">State</label>
									<select
										id="state"
										name="state"
										className="input-primary pl-4 pr-12"
										value={address.state}
										onChange={(e) =>
											onAddressChange(e.target.name, e.target.value)
										}
									>
										<option value=""> -- Select a state --</option>
										{Object.entries(currentUSStateCodes).map(
											([stateCode, stateName]) => (
												<option key={stateCode} value={stateCode}>
													{stateName}
												</option>
											)
										)}
									</select>

									<button
										className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
										onClick={nextStep}
										disabled={address.state !== 'MA' && address.state !== 'FL'}
									>
										Next
									</button>
								</div>
							)}
							{step === 3 && userType === 'caregiver' && (
								<>
									<div className="flex flex-col items-center gap-4 p-4 bg-white rounded-lg shadow">
										<div className="text-center text-lg font-semibold text-gray-800">
											Please enter your hourly rate.
										</div>

										<div className="relative w-60 mx-auto">
											<input
												className="input-primary w-24 pr-10 pl-2 py-2 text-center block mx-auto"
												type="number"
												min="1"
												onChange={(e) => setRate(e.target.value)}
											/>
											<span className="absolute right-1/2 translate-x-10 top-1/2 -translate-y-1/2 text-gray-700 pointer-events-none">
												$/h
											</span>
										</div>


										<div className="text-center text-gray-600">(Required)</div>
									</div>


									<div className="flex flex-col gap-4 p-4 bg-white rounded-lg shadow mt-6">
										<CategoriesPicker
											selected={categories}
											selectCategory={selectCategory}
											setCategoryRate={setCategoryRate}
											rate={rate}
										/>
									</div>

									<div className="mt-6">
										<div className="sticky top-4 bg-white p-4 rounded-lg shadow">
											<div className="mb-4">
												<div className="text-gray-800 font-medium mb-2">
													You can choose <b>{categories.length}</b> categories:
												</div>
												<div className="flex flex-wrap gap-3">
													{categories.map(({ title, index }) => (
														<div
															className="flex items-center gap-2 p-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 transition"
															key={`${title}-${index}`}
															onClick={() => selectCategory(title)}
														>
															<img src={IconCat} alt="Icon" className="w-5 h-5" />
															<span className="text-gray-700">{title}</span>
														</div>
													))}
												</div>
											</div>
											<button
												className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
												onClick={nextStep}
												// disabled={!(categories.length && rate)}
												disabled={!(categories.length && Number(rate) >= 0) && !rate}

											>
												Next
											</button>
										</div>
									</div>
								</>
							)}
							{step === 1 && userType === 'caregiver' && (
								<div className="flex flex-col items-center gap-6 w-full">
									<h4 className="text-lg font-semibold text-gray-800 text-center">
										Please enter your date of birth and work location.
									</h4>

									<div className="flex flex-col gap-1 w-full max-w-md  dob-register">
										<label htmlFor="dob" className="text-sm font-medium text-gray-700 mb-1">
											Date of Birth
										</label>
										<input
											id="dob"
											name="dob"
											type="date"
											className='input-primary w-full rounded-lg border-gray-300 pl-4 pr-4 py-2 shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-400 focus:outline-none transition duration-200'
											onChange={(e) => onDobChange(e.target.value)}
											value={dob}
										/>
									</div>


									<div className="w-full max-w-md space-y-4">
										<div>
											<label className="text-sm text-gray-600 block mb-1">Address Line 1</label>
											<input
												id="addressLine1"
												name="addressLine1"
												type="text"
												className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
												value={address.addressLine1}
												onInput={(e) => {
													onAddressChange(e.target.name, e.target.value);
													setIsAddressVerified(false)
												}}
											/>
										</div>

										<div>
											<label className="text-sm text-gray-600 block mb-1">Address Line 2</label>
											<input
												id="addressLine2"
												name="addressLine2"
												type="text"
												className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
												value={address.addressLine2}
												onInput={(e) => {
													onAddressChange(e.target.name, e.target.value);
													setIsAddressVerified(false)
												}}
											/>
										</div>

										<div className="flex flex-col md:flex-row gap-4">
											<div className="flex-1">
												<label className="text-sm text-gray-600 block mb-1">City</label>
												<input
													id="city"
													name="city"
													type="text"
													className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
													value={address.city}
													onInput={(e) => {
														onAddressChange(e.target.name, e.target.value);
														setIsAddressVerified(false)
													}}
												/>
											</div>

											<div className="flex-1">
												<label className="text-sm text-gray-600 block mb-1">State</label>
												<select
													id="state"
													name="state"
													className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
													value={address.state}
													onChange={(e) => {
														onAddressChange(e.target.name, e.target.value);
														setIsAddressVerified(false)
													}}
												>
													<option value=""> -- Select a state --</option>
													{Object.entries(currentUSStateCodes).map(
														([stateCode, stateName]) => (
															<option key={stateCode} value={stateCode}>
																{stateName}
															</option>
														)
													)}
												</select>

											</div>

											<div className="flex-1">
												<label className="text-sm text-gray-600 block mb-1">Zip Code</label>
												<input
													id="zipCode"
													name="zipCode"
													type="text"
													className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
													value={address.zipCode}
													onInput={(e) => {
														onAddressChange(e.target.name, e.target.value)
														setIsAddressVerified(false)
													}}
												/>
											</div>
										</div>

										<div>
											<label className="text-sm text-gray-600 block mb-1">Country</label>
											<input
												id="country"
												name="country"
												type="text"
												className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
												value={address.country}
												disabled
											/>
										</div>

										<div>
											<label className="text-sm text-gray-600 block mb-1">Country Code</label>
											<input
												id="countryCode"
												name="countryCode"
												type="text"
												className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
												value={address.countryCode}
												disabled
											/>
										</div>
									</div>

									<button
										className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50"
										disabled={isAddressVerified}
										onClick={verifyAddress}
									>
										Verify address
									</button>

									<button
										className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50"
										onClick={nextStep}
										disabled={!dob || !isAddressComplete() || !isAddressVerified}
									>
										Next
									</button>

									{error && <div className="text-red-500">{error.message}</div>}
									{successMessage && <div className="text-green-500">{successMessage}</div>}
								</div>
							)}
							{isLoading && <Loader show={isLoading} />}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Stepper;
